// Cloudflare Image Optimization Utilities
// These helpers enable Cloudflare's image optimization and resizing features
// ⚠️ IMPORTANT: Requires Cloudflare Image Resizing to be enabled in your account!
// Set REACT_APP_CLOUDFLARE_IMAGE_RESIZING=true in .env to enable

// Feature flag: Cloudflare Image Resizing must be explicitly enabled
const CF_IMAGE_RESIZING_ENABLED = process.env.REACT_APP_CLOUDFLARE_IMAGE_RESIZING === 'true';

if (!CF_IMAGE_RESIZING_ENABLED) {
  console.log('[Image Optimization] Cloudflare Image Resizing disabled. Set REACT_APP_CLOUDFLARE_IMAGE_RESIZING=true to enable.');
}

/**
 * Optimizes image URL using Cloudflare Image Resizing
 * @param {string} url - Original image URL
 * @param {object} options - Optimization options
 * @returns {string} - Optimized image URL or original if disabled
 */
export function optimizeImage(url, options = {}) {
  if (!url) return url;
  
  // If Cloudflare Image Resizing is disabled, return original URL
  if (!CF_IMAGE_RESIZING_ENABLED) {
    return url;
  }
  
  // Default options
  const {
    width = null,        // Target width
    height = null,       // Target height
    quality = 85,        // Quality 1-100 (85 is good balance)
    format = 'auto',     // 'auto', 'webp', 'avif', 'jpeg', 'png'
    fit = 'cover',       // 'cover', 'contain', 'crop', 'pad', 'scale-down'
  } = options;

  // Skip if not HTTP/HTTPS URL
  if (!url.startsWith('http')) return url;
  
  // Skip if already optimized
  if (url.includes('/cdn-cgi/image/')) return url;

  try {
    const urlObj = new URL(url);
    
    // Build Cloudflare Image Resizing parameters
    const params = [];
    if (width) params.push(`width=${width}`);
    if (height) params.push(`height=${height}`);
    if (quality) params.push(`quality=${quality}`);
    if (format) params.push(`format=${format}`);
    if (fit) params.push(`fit=${fit}`);
    
    // Add optimization flags
    params.push('onerror=redirect'); // Fallback to original on error
    
    const paramString = params.join(',');
    
    // Construct Cloudflare Image Resizing URL
    // Format: https://domain/cdn-cgi/image/{options}/{original-path}
    const optimizedUrl = `${urlObj.origin}/cdn-cgi/image/${paramString}${urlObj.pathname}${urlObj.search}`;
    
    return optimizedUrl;
  } catch (e) {
    console.warn('[Image Optimization] Failed to optimize URL:', e);
    return url; // Return original on error
  }
}

/**
 * Generate responsive image srcSet for different screen sizes
 * @param {string} url - Original image URL
 * @param {array} widths - Array of widths [320, 640, 960, 1280]
 * @returns {string} - srcSet string
 */
export function generateSrcSet(url, widths = [320, 640, 960, 1280, 1920]) {
  if (!url) return '';
  
  return widths
    .map(width => {
      const optimized = optimizeImage(url, { width, quality: 85, format: 'auto' });
      return `${optimized} ${width}w`;
    })
    .join(', ');
}

/**
 * Preload critical image with optimization
 * @param {string} url - Image URL to preload
 * @param {object} options - Optimization options
 */
export function preloadImage(url, options = {}) {
  if (!url || typeof document === 'undefined') return;
  
  const optimizedUrl = optimizeImage(url, options);
  
  // Check if already preloaded
  const existing = document.querySelector(`link[rel="preload"][href="${optimizedUrl}"]`);
  if (existing) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizedUrl;
  
  // Add responsive image hints
  if (options.width) {
    link.imageSrcset = generateSrcSet(url);
    link.imageSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
  }
  
  document.head.appendChild(link);
}

/**
 * Get optimized thumbnail for news cards
 * @param {string} url - Original image URL
 * @returns {string} - Optimized thumbnail URL
 */
export function getThumbnail(url) {
  return optimizeImage(url, {
    width: 400,
    height: 225,
    quality: 80,
    format: 'auto',
    fit: 'cover'
  });
}

/**
 * Get optimized hero image
 * @param {string} url - Original image URL
 * @returns {string} - Optimized hero image URL
 */
export function getHeroImage(url) {
  return optimizeImage(url, {
    width: 1920,
    quality: 85,
    format: 'auto',
    fit: 'cover'
  });
}

export default {
  optimizeImage,
  generateSrcSet,
  preloadImage,
  getThumbnail,
  getHeroImage
};
